import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Download, FileImage, FileSpreadsheet, FileText, Search, ShieldCheck, Upload } from "lucide-react";
import PerfectScrollbar from "react-perfect-scrollbar";
import Header from "../layouts/Header";
import UploadDocumentModal from "../components/modals/uploadDocumentModal";
import Spinner1 from "../components/common/spinners";
import useManageDocuments from "../hooks/patientDashboard/useManageDocuments";
import { formatDate } from "../utils/utilities";
import { Link } from "react-router-dom";

const ITEMS_PER_PAGE = 6;

const getDocumentPresentation = (fileName = "", documentType = "") => {
  const value = `${fileName} ${documentType}`.toLowerCase();

  if (value.includes("pdf")) {
    return {
      icon: FileText,
      iconClassName: "is-pdf",
      label: "PDF",
    };
  }

  if (value.includes("jpg") || value.includes("jpeg") || value.includes("png") || value.includes("image")) {
    return {
      icon: FileImage,
      iconClassName: "is-image",
      label: "Image",
    };
  }

  if (value.includes("xls") || value.includes("xlsx") || value.includes("sheet") || value.includes("csv")) {
    return {
      icon: FileSpreadsheet,
      iconClassName: "is-sheet",
      label: "Sheet",
    };
  }

  return {
    icon: FileText,
    iconClassName: "is-default",
    label: "Document",
  };
};

export default function MyDocuments() {
  const { loading, downloadState, handleFetchDocuments, handleDownloadDocument, documents } = useManageDocuments();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    handleFetchDocuments();
  }, []);

  useEffect(() => {
    document.body.classList.add("page-app");

    return () => {
      document.body.classList.remove("page-app");
    };
  }, []);

  const documentList = useMemo(() => {
    return Array.isArray(documents?.result) ? documents.result : [];
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return documentList;
    }

    return documentList.filter((item) => {
      return (
        item?.document_name?.toLowerCase().includes(term) ||
        item?.document_type?.toLowerCase().includes(term) ||
        item?.createdBy?.toLowerCase().includes(term)
      );
    });
  }, [documentList, searchTerm]);

  const recentDocuments = useMemo(() => {
    return documentList.slice(0, 4);
  }, [documentList]);

  const categoryCount = useMemo(() => {
    const categories = new Set(
      documentList.map((item) => item?.document_type).filter(Boolean)
    );

    return categories.size;
  }, [documentList]);

  const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDocuments.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, filteredDocuments]);

  const handleDownload = async (patientId, documentId, fileName) => {
    setDownloadingId(documentId);

    try {
      const response = await handleDownloadDocument(patientId, documentId);
      const url = window.URL.createObjectURL(new Blob([response?.url]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-4 p-lg-5">
        <PerfectScrollbar className="pe-lg-2">
          <div className="data-page-hero document-hero">
            <div className="data-page-copy">
              <span className="data-page-kicker">My Documents</span>
              <h2>Keep your health records organized, accessible, and easy to revisit</h2>
              <p>
                Browse uploaded files, search faster, and download what you need from a cleaner document workspace.
              </p>
            </div>
            <div className="data-page-stats">
              <div className="data-page-stat">
                <small>Total Files</small>
                <strong>{documentList.length}</strong>
              </div>
              <div className="data-page-stat">
                <small>Categories</small>
                <strong>{categoryCount}</strong>
              </div>
              <div className="data-page-stat">
                <small>Showing</small>
                <strong>{filteredDocuments.length}</strong>
              </div>
            </div>
          </div>

          <Card className="data-panel mb-4" style={{ borderLeft: "4px solid #3EB489", background: "#f0faf6" }}>
            <div className="d-flex align-items-center justify-content-between p-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: 8, background: "#3EB48910" }}>
                  <FileText size={18} color="#2D9B70" />
                </div>
                <div>
                  <div className="fw-semibold">Excuse Duty Certificates</div>
                  <div className="small text-muted">View and print your accepted certificates</div>
                </div>
              </div>
              <Link to="/page/excuse-duty" className="btn btn-primary btn-sm">View</Link>
            </div>
          </Card>

          <Row className="g-4">
            <Col xl={8}>
              <Card className="data-panel mb-4">
                <div className="data-panel-head">
                  <div className="data-panel-copy">
                    <h5>Recent Files</h5>
                    <p>The documents you are most likely to look for first.</p>
                  </div>
                  {/* <div className="data-panel-actions">
                    <Button variant="primary" onClick={() => setShow(true)}>
                      <Upload size={16} className="me-2" />
                      Upload document
                    </Button>
                  </div> */}
                </div>

                <div className="data-table-shell pt-3">
                  {loading ? (
                    <div className="w-100 d-flex justify-content-center py-5">
                      <Spinner1 />
                    </div>
                  ) : recentDocuments.length === 0 ? (
                    <div className="data-table-empty">
                      <h6>No recent files yet</h6>
                      <p>Upload your first document to start building your personal health library.</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {recentDocuments.map((file) => {
                        const presentation = getDocumentPresentation(file?.document_name, file?.document_type);
                        const Icon = presentation.icon;

                        return (
                          <Col md={6} key={file?.id}>
                            <div className="document-card h-100">
                              <div className={`document-card-icon ${presentation.iconClassName}`}>
                                <Icon size={20} />
                              </div>
                              <div className="document-card-body">
                                <div className="document-card-topline">
                                  <span className="data-status-badge is-neutral">{presentation.label}</span>
                                  <span className="data-cell-meta">{formatDate(file?.createdAt, "DD-MMM-YYYY")}</span>
                                </div>
                                <h6>{file?.document_name || "Untitled document"}</h6>
                                <p>{file?.document_type || "General document"}</p>
                                <Button
                                  variant="light"
                                  className="document-card-action"
                                  onClick={() => handleDownload(file?.patient_id, file?.id, file?.document_name)}
                                  disabled={downloadingId === file?.id || downloadState}
                                >
                                  <Download size={16} className="me-2" />
                                  {downloadingId === file?.id ? "Downloading..." : "Download"}
                                </Button>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              </Card>

            
            </Col>

            <Col xl={4}>
              <div className="data-sidebar-stack">
                <div className="data-sidebar-card">
                  <h6 className="data-sidebar-title">Document Summary</h6>
                  <div className="document-summary-grid">
                    <div className="document-summary-item">
                      <span className="document-summary-icon is-accent">
                        <FileText size={18} />
                      </span>
                      <div>
                        <strong>{documentList.length}</strong>
                        <p>records stored</p>
                      </div>
                    </div>
                    <div className="document-summary-item">
                      <span className="document-summary-icon is-warm">
                        <Search size={18} />
                      </span>
                      <div>
                        <strong>{filteredDocuments.length}</strong>
                        <p>matching search</p>
                      </div>
                    </div>
                    <div className="document-summary-item">
                      <span className="document-summary-icon is-cool">
                        <ShieldCheck size={18} />
                      </span>
                      <div>
                        <strong>{categoryCount}</strong>
                        <p>document groups</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="data-sidebar-card d-none">
                  <h6 className="data-sidebar-title">Quick Notes</h6>
                  <div className="data-sidebar-stack">
                    <div className="data-sidebar-item">
                      <div className="data-cell-title">Supported files</div>
                      <div className="data-cell-meta">PDF, Word documents, and text files can be uploaded here.</div>
                    </div>
                    <div className="data-sidebar-item">
                      <div className="data-cell-title">Better search</div>
                      <div className="data-cell-meta">
                        Search by file name, category, or the user who uploaded the document.
                      </div>
                    </div>
                    <div className="data-sidebar-item">
                      <div className="data-cell-title">Fast access</div>
                      <div className="data-cell-meta">
                        Recent files stay at the top so your most-used records are easier to reach.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

              <Card className="data-panel">
                <div className="data-panel-head">
                  <div className="data-panel-copy">
                    <h5>Document Library</h5>
                    <p>Search across uploaded files and download any record you need.</p>
                  </div>
                  {/* <div className="data-panel-actions document-toolbar">
                    <div className="data-search">
                      <Form.Control
                        type="text"
                        placeholder="Search by name, type, or uploader"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                    </div>
                    <Button variant="primary" onClick={() => setShow(true)}>
                      <Upload size={16} className="me-2" />
                      Add file
                    </Button>
                  </div> */}
                </div>

                <div className="data-table-shell">
                  {loading ? (
                    <div className="w-100 d-flex justify-content-center py-5">
                      <Spinner1 />
                    </div>
                  ) : currentItems.length === 0 ? (
                    <div className="data-table-empty">
                      <h6>No documents found</h6>
                      <p>Try a different search term or upload a new file.</p>
                    </div>
                  ) : (
                    <>
                      <div className="data-cell-meta mb-3">
                        Page {currentPage} of {totalPages} · {filteredDocuments.length} matching document
                        {filteredDocuments.length === 1 ? "" : "s"}
                      </div>
                      <div className="data-table-wrap">
                        <table className="table table-agent data-table mb-0">
                          <thead>
                            <tr>
                              <th>Document</th>
                              <th>ID</th>
                              <th>Type</th>
                              <th>Uploaded By</th>
                              <th>Date</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.map((doc) => {
                              const presentation = getDocumentPresentation(doc?.document_name, doc?.document_type);
                              const Icon = presentation.icon;

                              return (
                                <tr key={doc?.id}>
                                  <td>
                                    <div className="d-flex align-items-center gap-3">
                                      <span className={`document-mini-icon ${presentation.iconClassName}`}>
                                        <Icon size={16} />
                                      </span>
                                      <div>
                                        <div className="data-cell-title">{doc?.document_name || "---"}</div>
                                        <div className="data-cell-meta">{presentation.label}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="data-cell-title">#{doc?.id || "---"}</span>
                                  </td>
                                  <td>
                                    <span className="data-status-badge is-neutral">
                                      {doc?.document_type || "---"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="data-cell-title">{doc?.createdBy || "---"}</div>
                                  </td>
                                  <td>
                                    <div className="data-cell-meta">
                                      {formatDate(doc?.createdAt, "DD-MMM-YYYY h:mm A")}
                                    </div>
                                  </td>
                                  <td className="text-end">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip id={`document-download-${doc?.id}`}>Download</Tooltip>}
                                    >
                                      <Button
                                        variant="light"
                                        className="data-action-btn"
                                        onClick={() => handleDownload(doc?.patient_id, doc?.id, doc?.document_name)}
                                        disabled={downloadingId === doc?.id || downloadState}
                                      >
                                        <Download size={16} />
                                      </Button>
                                    </OverlayTrigger>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {!loading && filteredDocuments.length > 0 && (
                    <div className="data-pagination">
                      <Button
                        variant="light"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((page) => page - 1)}
                      >
                        Previous
                      </Button>
                      <span>Page {currentPage} of {totalPages}</span>
                      <Button
                        variant="light"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((page) => page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
          </Row>
        </PerfectScrollbar>

        <UploadDocumentModal
          setShow={setShow}
          show={show}
          onUploaded={async () => {
            await handleFetchDocuments();
          }}
        />
      </div>
    </React.Fragment>
  );
}
